import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import { Check, ChevronsUpDown, Loader2, Search, Package, Ruler } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SatProductSearch - Búsqueda de claves de producto SAT
 * Busca por clave o descripción con filtros activos
 */
export const SatProductSearch = ({ value, onChange, onDescriptionSelect, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/sat/products?q=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error("Error searching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar producto seleccionado si hay un valor inicial
  useEffect(() => {
    if (value && !selectedProduct) {
      const fetchProduct = async () => {
        try {
          const token = localStorage.getItem("cia_token");
          const response = await axios.get(`${API}/sat/products?q=${value}&limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const found = response.data.products?.find(p => p.clave === value);
          if (found) {
            setSelectedProduct(found);
          }
        } catch (error) {
          console.error("Error fetching product:", error);
        }
      };
      fetchProduct();
    }
  }, [value, selectedProduct]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchProducts]);

  const handleSelect = (product) => {
    onChange(product.clave);
    setSelectedProduct(product);
    onDescriptionSelect?.(product.descripcion);
    setOpen(false);
    setSearchTerm("");
  };

  // Determinar si la búsqueda es por clave (números) o descripción (texto)
  const isSearchingByCode = /^\d+$/.test(searchTerm);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between text-left font-normal h-auto min-h-[40px] py-2",
            !value && "text-muted-foreground",
            className
          )}
          data-testid="sat-product-search"
        >
          <div className="flex items-center gap-2 flex-1 overflow-hidden">
            <Package className="h-4 w-4 shrink-0 text-slate-400" />
            {value ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <Badge variant="secondary" className="font-mono text-xs shrink-0 bg-blue-100 text-blue-700">
                  {value}
                </Badge>
                <span className="text-sm truncate text-slate-600">
                  {selectedProduct?.descripcion || ""}
                </span>
              </div>
            ) : (
              <span className="text-sm">{placeholder || "Buscar clave o descripción..."}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[450px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput 
              placeholder="Escribe clave (ej: 43231500) o descripción..." 
              value={searchTerm}
              onValueChange={setSearchTerm}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-[300px] overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center p-4 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                <span className="text-sm text-slate-500">Buscando...</span>
              </div>
            )}
            {!loading && products.length === 0 && searchTerm.length >= 2 && (
              <CommandEmpty className="py-6 text-center">
                <Package className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No se encontró el producto</p>
                <p className="text-xs text-slate-400 mt-1">
                  {isSearchingByCode 
                    ? `No existe la clave "${searchTerm}"` 
                    : `Sin resultados para "${searchTerm}"`}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => {
                    onChange("01010101");
                    setSelectedProduct({ clave: "01010101", descripcion: "No existe en el catálogo" });
                    setOpen(false);
                    setSearchTerm("");
                  }}
                >
                  Usar "01010101 - No existe en el catálogo"
                </Button>
              </CommandEmpty>
            )}
            {!loading && searchTerm.length < 2 && (
              <div className="py-6 text-center text-sm text-slate-500">
                <Search className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p>Escribe al menos 2 caracteres</p>
                <p className="text-xs text-slate-400 mt-1">Puedes buscar por clave o descripción</p>
              </div>
            )}
            {!loading && products.length > 0 && (
              <CommandGroup heading={`${products.length} resultados`}>
                {products.map((product) => (
                  <CommandItem
                    key={product.clave}
                    value={product.clave}
                    onSelect={() => handleSelect(product)}
                    className="flex items-start gap-3 p-3 cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 mt-0.5 shrink-0",
                        value === product.clave ? "opacity-100 text-green-600" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="font-mono text-xs bg-blue-500 hover:bg-blue-600">
                          {product.clave}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-700 leading-tight">
                        {product.descripcion}
                      </p>
                      {product.palabras_similares && (
                        <p className="text-xs text-slate-400 mt-1 truncate">
                          Similar: {product.palabras_similares}
                        </p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * SatUnitSearch - Búsqueda de claves de unidad SAT
 * Busca por clave o nombre con filtros activos
 */
export const SatUnitSearch = ({ value, onChange, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const searchUnits = useCallback(async (query) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("cia_token");
      const url = query 
        ? `${API}/sat/units?q=${encodeURIComponent(query)}&limit=20` 
        : `${API}/sat/units?q=&limit=20`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(response.data.units || []);
    } catch (error) {
      console.error("Error searching units:", error);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar unidad seleccionada si hay un valor inicial
  useEffect(() => {
    if (value && !selectedUnit) {
      const fetchUnit = async () => {
        try {
          const token = localStorage.getItem("cia_token");
          const response = await axios.get(`${API}/sat/units?q=${value}&limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const found = response.data.units?.find(u => u.clave === value);
          if (found) {
            setSelectedUnit(found);
          }
        } catch (error) {
          console.error("Error fetching unit:", error);
        }
      };
      fetchUnit();
    }
  }, [value, selectedUnit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchUnits(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchUnits]);

  // Cargar unidades comunes al abrir
  useEffect(() => {
    if (open && units.length === 0) {
      searchUnits("");
    }
  }, [open, units.length, searchUnits]);

  const handleSelect = (unit) => {
    onChange(unit.clave);
    setSelectedUnit(unit);
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between text-left font-normal h-auto min-h-[40px] py-2",
            !value && "text-muted-foreground",
            className
          )}
          data-testid="sat-unit-search"
        >
          <div className="flex items-center gap-2 flex-1 overflow-hidden">
            <Ruler className="h-4 w-4 shrink-0 text-slate-400" />
            {value ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <Badge variant="secondary" className="font-mono text-xs shrink-0 bg-green-100 text-green-700">
                  {value}
                </Badge>
                <span className="text-sm truncate text-slate-600">
                  {selectedUnit?.nombre || ""}
                </span>
              </div>
            ) : (
              <span className="text-sm">{placeholder || "Buscar unidad..."}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput 
              placeholder="Escribe clave (ej: H87) o nombre..." 
              value={searchTerm}
              onValueChange={setSearchTerm}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandList className="max-h-[300px] overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center p-4 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-green-500" />
                <span className="text-sm text-slate-500">Buscando...</span>
              </div>
            )}
            {!loading && units.length === 0 && searchTerm.length >= 1 && (
              <CommandEmpty className="py-6 text-center">
                <Ruler className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">No se encontró la unidad</p>
                <p className="text-xs text-slate-400 mt-1">Intenta con otra clave o nombre</p>
              </CommandEmpty>
            )}
            {!loading && units.length > 0 && (
              <CommandGroup heading={`${units.length} unidades`}>
                {units.map((unit) => (
                  <CommandItem
                    key={unit.clave}
                    value={unit.clave}
                    onSelect={() => handleSelect(unit)}
                    className="flex items-start gap-3 p-3 cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 mt-0.5 shrink-0",
                        value === unit.clave ? "opacity-100 text-green-600" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="font-mono text-xs bg-green-500 hover:bg-green-600">
                          {unit.clave}
                        </Badge>
                        <span className="text-sm font-medium text-slate-700">{unit.nombre}</span>
                      </div>
                      {unit.descripcion && unit.descripcion !== unit.nombre && (
                        <p className="text-xs text-slate-500 truncate">
                          {unit.descripcion}
                        </p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default { SatProductSearch, SatUnitSearch };
